const SEARCH_DELAY = 250;
const SEARCH_MIN_CHARS = 2;

export function initSelects(container = document) {
  const selects = container.querySelectorAll('.select-custom');

  selects.forEach(select => {
    if (select.dataset.selectBound) return;
    select.dataset.selectBound = 'true';

    const trigger = select.querySelector('.select-custom__trigger');
    const dropdown = select.querySelector('.select-custom__dropdown');
    const search = select.querySelector('.select-custom__search');
    const optionsList = select.querySelector('.select-custom__options');
    const valueDisplay = select.querySelector('.select-custom__value');
    const hiddenInput = select.querySelector('.select-custom__input');
    const isMultiple = select.classList.contains('select-custom--multiple');
    const isRemote = isMultiple && select.hasAttribute('data-remote') && !!search;
    const allowCreate = isMultiple && select.hasAttribute('data-allow-create') && !!search;
    const createPattern = select.dataset.createPattern ? new RegExp(select.dataset.createPattern) : null;
    const createError = select.dataset.createError || 'Valeur invalide';
    const max = parseInt(select.dataset.max, 10) || Infinity;

    let selectedValues = isMultiple ? [] : null;
    let currentFocusIndex = -1;
    let searchTimer = null;
    let pendingTerm = null;

    const getOptions = () => Array.from(select.querySelectorAll('.select-custom__option'));
    const isGroupLabel = (o) => o.classList.contains('select-custom__group-label');
    const optionLabel = (o) => o.querySelector('.select-custom__option-label')?.textContent.trim() || o.textContent.trim();

    // Les valeurs libres (allow-create) ignorent la casse : Jean.Dupont@x.fr = jean.dupont@x.fr
    const sameValue = (a, b) => a === b || (allowCreate && a.toLowerCase() === b.toLowerCase());
    const findSelectedIndex = (value) => selectedValues.findIndex(v => sameValue(v.value, value));

    getOptions().forEach(opt => {
      if (!isGroupLabel(opt)) opt.setAttribute('tabindex', '-1');
    });

    // Initialiser l'état depuis les options pré-sélectionnées dans le HTML
    const preselected = getOptions().filter(o => o.classList.contains('select-custom__option--selected'));
    if (preselected.length) {
      if (isMultiple) {
        selectedValues = preselected.map(o => ({ value: o.getAttribute('data-value'), label: optionLabel(o) }));
      } else {
        const o = preselected[0];
        selectedValues = { value: o.getAttribute('data-value'), label: optionLabel(o) };
      }
    }

    // ---------- Messages (recherche, erreur de saisie, limite) ----------

    // Zone d'état de la recherche distante : visible pour « Recherche… », lue seule pour « N résultats »
    let statusEl = null;
    if (isRemote) {
      statusEl = document.createElement('div');
      statusEl.className = 'select-custom__no-results select-custom__status';
      statusEl.setAttribute('role', 'status');
      statusEl.setAttribute('aria-live', 'polite');
      optionsList.before(statusEl);
    }

    function setStatus(text, { visible = true } = {}) {
      if (!statusEl) return;
      statusEl.textContent = text;
      statusEl.classList.toggle('sr-only', !visible);
    }

    function setFeedback(state, text) {
      ['error', 'warning'].forEach(s => {
        select.classList.remove(`select-custom--${s}`);
        select.querySelector(`.select-custom__${s}[data-select-feedback]`)?.remove();
      });
      if (!state) return;
      const el = document.createElement('span');
      el.className = `select-custom__${state}`;
      el.dataset.selectFeedback = '';
      el.setAttribute('role', state === 'error' ? 'alert' : 'status');
      el.textContent = text;
      dropdown.after(el);
      select.classList.add(`select-custom--${state}`);
    }

    // ---------- Navigation ----------

    function getVisibleOptions() {
      return getOptions().filter(opt => !isGroupLabel(opt) && opt.style.display !== 'none');
    }

    function setOptionFocus(index) {
      const visible = getVisibleOptions();
      if (visible.length === 0) return;
      if (index < 0) index = 0;
      if (index >= visible.length) index = visible.length - 1;

      visible.forEach(o => o.setAttribute('tabindex', '-1'));
      const target = visible[index];
      target.setAttribute('tabindex', '0');
      target.focus();
      currentFocusIndex = index;
    }

    function openSelect() {
      container.querySelectorAll('.select-custom--open').forEach(s => {
        s.classList.remove('select-custom--open');
        s.querySelector('.select-custom__trigger')?.setAttribute('aria-expanded', 'false');
      });

      // Ouvre vers le haut si pas assez de place en bas
      const triggerRect = trigger.getBoundingClientRect();
      const dropdownHeight = dropdown.scrollHeight || dropdown.offsetHeight || 300;
      const spaceBelow = window.innerHeight - triggerRect.bottom;
      const spaceAbove = triggerRect.top;
      const margin = 16;
      const dropUp = spaceBelow < dropdownHeight + margin && spaceAbove > spaceBelow;
      select.classList.toggle('select-custom--drop-up', dropUp);

      select.classList.add('select-custom--open');
      trigger.setAttribute('aria-expanded', 'true');

      if (isRemote && !search.value.trim()) setStatus(`Saisissez au moins ${SEARCH_MIN_CHARS} caractères`);

      if (search) {
        search.focus();
      } else {
        const visible = getVisibleOptions();
        const selectedIndex = visible.findIndex(o => o.classList.contains('select-custom__option--selected'));
        setOptionFocus(selectedIndex >= 0 ? selectedIndex : 0);
      }
    }

    function closeSelect() {
      select.classList.remove('select-custom--open');
      trigger.setAttribute('aria-expanded', 'false');
      currentFocusIndex = -1;
      getVisibleOptions().forEach(o => o.setAttribute('tabindex', '-1'));
    }

    trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      if (!select.classList.contains('select-custom--open')) openSelect();
      else closeSelect();
    });

    trigger.addEventListener('keydown', (e) => {
      const isOpen = select.classList.contains('select-custom--open');
      switch (e.key) {
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (!isOpen) openSelect();
          break;
        case 'ArrowDown':
          e.preventDefault();
          if (!isOpen) openSelect();
          else setOptionFocus(0);
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (!isOpen) openSelect();
          else setOptionFocus(getVisibleOptions().length - 1);
          break;
        case 'Escape':
          if (isOpen) {
            closeSelect();
            trigger.focus();
          }
          break;
      }
    });

    // ---------- Recherche ----------

    function filterLocally(term) {
      let has = false;
      getOptions().forEach(opt => {
        if (isGroupLabel(opt)) return;
        const match = opt.textContent.toLowerCase().includes(term);
        opt.style.display = match ? 'flex' : 'none';
        if (match) has = true;
      });

      let noResults = dropdown.querySelector('.select-custom__no-results:not(.select-custom__status)');
      if (!has) {
        if (!noResults) {
          noResults = document.createElement('div');
          noResults.className = 'select-custom__no-results';
          noResults.setAttribute('role', 'status');
          noResults.setAttribute('aria-live', 'polite');
          noResults.textContent = 'Aucun résultat trouvé';
          optionsList.appendChild(noResults);
        }
        noResults.style.display = 'block';
      } else if (noResults) {
        noResults.style.display = 'none';
      }
    }

    function searchRemotely(term) {
      clearTimeout(searchTimer);
      if (term.length < SEARCH_MIN_CHARS) {
        pendingTerm = null;
        renderOptions([]);
        setStatus(`Saisissez au moins ${SEARCH_MIN_CHARS} caractères`);
        return;
      }
      setStatus('Recherche…');
      searchTimer = setTimeout(() => {
        pendingTerm = term;
        select.dispatchEvent(new CustomEvent('ds-select:search', { bubbles: true, detail: { term } }));
      }, SEARCH_DELAY);
    }

    function resetSearch() {
      if (!search) return;
      search.value = '';
      clearTimeout(searchTimer);
      if (isRemote) {
        pendingTerm = null;
        renderOptions([]);
        setStatus('');
      } else {
        getOptions().forEach(opt => {
          if (!isGroupLabel(opt)) opt.style.display = 'flex';
        });
        const noResults = dropdown.querySelector('.select-custom__no-results:not(.select-custom__status)');
        if (noResults) noResults.style.display = 'none';
      }
    }

    if (search) {
      search.addEventListener('input', (e) => {
        if (select.classList.contains('select-custom--error')) setFeedback(null);
        if (isRemote) searchRemotely(e.target.value.trim());
        else filterLocally(e.target.value.toLowerCase());
        currentFocusIndex = -1;
        getVisibleOptions().forEach(o => o.setAttribute('tabindex', '-1'));
      });

      search.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setOptionFocus(0);
        } else if (e.key === 'Escape') {
          closeSelect();
          trigger.focus();
        } else if (e.key === 'Enter' && allowCreate) {
          e.preventDefault();
          createFromSearch();
        } else if (e.key === 'Backspace' && isMultiple && search.value === '' && selectedValues.length) {
          removeSelection(selectedValues[selectedValues.length - 1].value);
        }
      });
    }

    // ---------- Options distantes ----------

    function renderOptions(list) {
      optionsList.querySelectorAll('.select-custom__option').forEach(o => o.remove());
      list.forEach(({ value, label, description }) => {
        const li = document.createElement('li');
        li.className = 'select-custom__option';
        li.setAttribute('role', 'option');
        li.setAttribute('tabindex', '-1');
        li.dataset.value = value;

        const text = document.createElement('span');
        text.className = 'select-custom__option-text';
        const labelEl = document.createElement('span');
        labelEl.className = 'select-custom__option-label';
        labelEl.textContent = label ?? value;
        text.appendChild(labelEl);
        if (description) {
          const desc = document.createElement('span');
          desc.className = 'select-custom__option-description';
          desc.textContent = description;
          text.appendChild(desc);
        }
        li.appendChild(text);

        const selected = findSelectedIndex(String(value)) > -1;
        li.classList.toggle('select-custom__option--selected', selected);
        li.setAttribute('aria-selected', String(selected));
        optionsList.appendChild(li);
      });
      currentFocusIndex = -1;
    }

    // Remplace la liste affichée. Avec `term`, une réponse arrivée pour une ancienne saisie est ignorée.
    select.setOptions = (list = [], term) => {
      if (term !== undefined && term !== pendingTerm) return;
      renderOptions(list.map(o => ({ ...o, value: String(o.value) })));
      if (!isRemote) return;
      const n = list.length;
      if (n === 0) setStatus('Aucun résultat');
      else setStatus(`${n} résultat${n > 1 ? 's' : ''}`, { visible: false });
    };

    // ---------- Sélection ----------

    function syncOptions() {
      getOptions().forEach(o => {
        if (isGroupLabel(o)) return;
        const value = o.getAttribute('data-value');
        const selected = isMultiple
          ? findSelectedIndex(value) > -1
          : selectedValues?.value === value;
        o.classList.toggle('select-custom__option--selected', selected);
        o.setAttribute('aria-selected', String(selected));
      });
    }

    function emitChange() {
      select.dispatchEvent(new CustomEvent('change', { detail: { value: selectedValues } }));
      const selected = isMultiple ? [...selectedValues] : (selectedValues ? [selectedValues] : []);
      select.dispatchEvent(new CustomEvent('ds-select:change', {
        bubbles: true,
        detail: {
          value: isMultiple ? selected.map(v => v.value) : (selectedValues?.value ?? null),
          selected
        }
      }));
    }

    function refresh() {
      syncOptions();
      if (isMultiple) {
        updateMultipleDisplay();
        if (selectedValues.length < max && select.classList.contains('select-custom--warning')) setFeedback(null);
      } else if (valueDisplay) {
        valueDisplay.textContent = selectedValues?.label || valueDisplay.dataset.placeholder || valueDisplay.textContent;
        valueDisplay.classList.toggle('select-custom__value--placeholder', !selectedValues);
      }
      updateHiddenInput();
    }

    function addSelection(value, label) {
      if (findSelectedIndex(value) > -1) return false;
      if (selectedValues.length >= max) {
        setFeedback('warning', `Limite de ${max} sélections atteinte`);
        return false;
      }
      selectedValues.push({ value, label });
      return true;
    }

    function removeSelection(value) {
      const idx = findSelectedIndex(value);
      if (idx === -1) return;
      selectedValues.splice(idx, 1);
      refresh();
      emitChange();
    }

    function createFromSearch() {
      const raw = search.value.trim();
      if (!raw) return;

      // Une option affichée correspond exactement à la saisie : on la sélectionne plutôt que de créer
      const match = getVisibleOptions().find(o =>
        o.getAttribute('data-value').toLowerCase() === raw.toLowerCase() ||
        optionLabel(o).toLowerCase() === raw.toLowerCase());
      if (match) {
        if (findSelectedIndex(match.getAttribute('data-value')) === -1) handleOptionSelect(match);
        resetSearch();
        return;
      }

      if (createPattern && !createPattern.test(raw)) {
        setFeedback('error', createError);
        return;
      }
      if (findSelectedIndex(raw) > -1) {
        resetSearch();
        return;
      }
      if (addSelection(raw, raw)) {
        setFeedback(null);
        refresh();
        emitChange();
      }
      resetSearch();
      if (isRemote) setStatus(`Saisissez au moins ${SEARCH_MIN_CHARS} caractères`);
    }

    function handleOptionSelect(option) {
      if (option.classList.contains('select-custom__option--disabled')) return;
      const value = option.getAttribute('data-value');
      const label = optionLabel(option);

      if (isMultiple) {
        const idx = findSelectedIndex(value);
        if (idx > -1) selectedValues.splice(idx, 1);
        else if (!addSelection(value, label)) return;
        refresh();
      } else {
        selectedValues = { value, label };
        refresh();
        closeSelect();
        trigger.focus();
      }
      emitChange();
    }

    // Délégation : les options peuvent être remplacées par setOptions()
    optionsList.addEventListener('click', (e) => {
      const option = e.target.closest('.select-custom__option');
      if (option && !isGroupLabel(option)) handleOptionSelect(option);
    });

    optionsList.addEventListener('keydown', (e) => {
      const option = e.target.closest('.select-custom__option');
      if (!option || isGroupLabel(option)) return;
      const visible = getVisibleOptions();
      const idx = visible.indexOf(option);

      switch (e.key) {
        case 'Enter':
        case ' ':
          e.preventDefault();
          handleOptionSelect(option);
          break;
        case 'ArrowDown':
          e.preventDefault();
          setOptionFocus(idx + 1);
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (idx > 0) setOptionFocus(idx - 1);
          else if (search) {
            search.focus();
            currentFocusIndex = -1;
          }
          break;
        case 'Home':
          e.preventDefault();
          setOptionFocus(0);
          break;
        case 'End':
          e.preventDefault();
          setOptionFocus(visible.length - 1);
          break;
        case 'Escape':
          e.preventDefault();
          closeSelect();
          trigger.focus();
          break;
        case 'Tab':
          closeSelect();
          break;
      }
    });

    function updateMultipleDisplay() {
      let tagsContainer = select.querySelector('.select-custom__tags');

      if (!tagsContainer) {
        tagsContainer = document.createElement('div');
        tagsContainer.className = 'select-custom__tags';
        tagsContainer.setAttribute('role', 'list');
        trigger.insertBefore(tagsContainer, trigger.firstChild);
      }

      tagsContainer.replaceChildren();

      selectedValues.forEach(item => {
        // textContent : libellés issus de résultats distants ou de saisie libre
        const badge = document.createElement('span');
        badge.className = 'badge badge-xs';
        badge.setAttribute('role', 'listitem');

        const label = document.createElement('span');
        label.className = 'badge__label';
        label.textContent = item.label;

        const dismiss = document.createElement('button');
        dismiss.type = 'button';
        dismiss.className = 'badge__dismiss';
        dismiss.setAttribute('aria-label', `Retirer ${item.label}`);
        dismiss.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>';
        dismiss.addEventListener('click', (e) => {
          e.stopPropagation();
          removeSelection(item.value);
        });

        badge.append(label, dismiss);
        tagsContainer.appendChild(badge);
      });

      valueDisplay?.classList.toggle('select-custom__value--placeholder', selectedValues.length === 0);
    }

    function updateHiddenInput() {
      if (!hiddenInput) return;
      hiddenInput.value = isMultiple
        ? selectedValues.map(v => v.value).join(',')
        : (selectedValues?.value || '');
    }

    // ---------- API ----------

    if (valueDisplay && !valueDisplay.dataset.placeholder && !selectedValues) {
      valueDisplay.dataset.placeholder = valueDisplay.textContent.trim();
    }

    // Remplace les sélections (libellés compris), y compris des valeurs absentes des options.
    select.setSelected = (list = []) => {
      const items = list.map(v => ({ value: String(v.value), label: v.label ?? String(v.value) }));
      if (isMultiple) {
        selectedValues = [];
        items.forEach(v => {
          if (findSelectedIndex(v.value) === -1 && selectedValues.length < max) selectedValues.push(v);
        });
      } else {
        selectedValues = items[0] || null;
      }
      setFeedback(null);
      refresh();
      emitChange();
    };

    select.clear = () => select.setSelected([]);

    if (isMultiple && selectedValues.length) updateMultipleDisplay();

    document.addEventListener('click', (e) => {
      if (!select.contains(e.target)) {
        closeSelect();
        resetSearch();
      }
    });
  });
}
